import eventMixin from '../lib/mixins/event';
import Logger from '../lib/logger';
import KeenSlider from 'keen-slider';

export default class ThemeCarouselBanner {

    constructor(dom) {

        // Vars
        // ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
        this.NAME = `ThemeCarouselBanner`;

        this.dom = dom;
        this.dotAttribute = `data-theme-carousel-dot`;
        this.arrowAttribute = `data-theme-carousel-arrow`;
        this.dotDomActiveClass = `theme-carousel-banner__dot--active`;
        this.dotDoms = this.dom.querySelectorAll(`[${this.dotAttribute}]`);
        this.arrowDoms = this.dom.querySelectorAll(`[${this.arrowAttribute}]`);
        this.slidesContainerDom = this.dom.querySelector(`[data-theme-carousel-banner-slides]`);
        this.autoplayEnabled = this.dom.dataset.themeCarouselAutoplay == 'true';
        this.autoplayInterval = parseInt(this.dom.dataset.themeCarouselAutoplayInterval, 10) * 1000 || 5000;

        this.slider;
    }

    // Logic
    // ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
    init() {
        Logger.info(`${this.NAME} > init`);

        const self = this;

        /**
        * Initialize the carousel when visible
        */
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const intersecting = entry.isIntersecting;

                if (intersecting && !this.slider) {
                    self.initializeCarousel();
                }
            })
        })

        observer.observe(this.dom);
    }

    /**
     * initializeCarousel - Initialize the Carousel
     */
    initializeCarousel() {
        Logger.info(`${this.NAME} > initializeCarousel`);

        if (this.slider || !this.slidesContainerDom) return;

        const self = this;

        this.slider = new KeenSlider(
            this.slidesContainerDom,
            {
                selector: '.theme-carousel-banner__slide',
                loop: true
            },
            [
                (slider) => {
                    let autoplayTimeout;
                    let autoplayMouseOver = false;

                    const clearAutoplayTimeout = () => {
                        clearTimeout(autoplayTimeout);
                    };

                    const nextAutoplayTimeout = () => {
                        clearAutoplayTimeout();

                        if (!self.autoplayEnabled || autoplayMouseOver) return;

                        autoplayTimeout = setTimeout(() => {
                            slider.next();
                        }, self.autoplayInterval);
                    };

                    // After slider is created
                    slider.on('created', () => {
                        Logger.info(`${this.NAME} > init > created`);

                        // Initialize all dots for clicking
                        self.dotDoms.forEach((dotDom) => {
                            dotDom.addEventListener('click', (e) => {
                                e.preventDefault();
                                const idx = dotDom.getAttribute(self.dotAttribute);
                                slider.moveToIdx(parseInt(idx));
                            });
                        });

                        // Initialize all arrows for clicking when the section setting enables them.
                        self.arrowDoms.forEach((arrowDom) => {
                            arrowDom.addEventListener('click', (e) => {
                                e.preventDefault();
                                const direction = arrowDom.getAttribute(self.arrowAttribute);

                                if (direction == 'next') {
                                    slider.next();
                                } else {
                                    slider.prev();
                                }
                            });
                        });

                        if (self.autoplayEnabled) {
                            self.dom.addEventListener('mouseover', () => {
                                autoplayMouseOver = true;
                                clearAutoplayTimeout();
                            });

                            self.dom.addEventListener('mouseout', () => {
                                autoplayMouseOver = false;
                                nextAutoplayTimeout();
                            });
                        }

                        self.updateClasses(slider);
                        nextAutoplayTimeout();
                    });

                    // After a slide has changed
                    slider.on('slideChanged', () => {
                        Logger.info(`${this.NAME} > init > slideChanged`);
                        self.updateClasses(slider)
                    });

                    slider.on('dragStarted', clearAutoplayTimeout);
                    slider.on('animationEnded', nextAutoplayTimeout);
                    slider.on('updated', nextAutoplayTimeout);
                }
            ]
        );
    }

    /**
     * updateClasses - Update all classes according to the slider's state
     * @param {*} slider 
     */
    updateClasses(slider) {
        Logger.info(`${this.NAME} > updateClasses`);

        const self = this;

        const idx = slider.track.details.rel;

        self.dotDoms.forEach((dotDom) => {
            const isActive = parseInt(dotDom.getAttribute(this.dotAttribute)) == idx;

            if (isActive) {
                dotDom.classList.add(self.dotDomActiveClass);
            } else {
                dotDom.classList.remove(self.dotDomActiveClass);
            }

            dotDom.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
    }
}

// Mixins
Object.assign(ThemeCarouselBanner.prototype, eventMixin);
